/** biome-ignore-all lint/a11y/noStaticElementInteractions: -- */

import { useState } from "react";
import { Link } from "react-router-dom";
import { PanelLeftIcon } from "lucide-react";

import { IconLogo, Logo } from "@ospra/design-system/components/logo";
import {
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarSeparator,
} from "@ospra/design-system/components/sidebar";
import { cn } from "@ospra/design-system/lib/utils";

import { getNavConfig } from "@/config/nav";
import { useUnreadCount } from "@/features/Inbox/service/query";
import { useAuth } from "@/hooks/useAuth";

import { NavItems } from "./navItems";
import { NavUser } from "./navUser";

export function AppSidebar() {
  const { user } = useAuth();
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.data.unreadCount ?? 0;
  const nav = getNavConfig(unreadCount);

  const [isPinned, setIsPinned] = useState(true);
  const [isHovering, setIsHovering] = useState(false);

  const showFull = isPinned || isHovering;
  const isOverlay = !isPinned && isHovering;

  function handleToggle() {
    setIsPinned((p) => !p);
    setIsHovering(false);
  }

  return (
    <>
      {/* Layout spacer — drives reflow; only changes when pinned state changes */}
      <div
        className="shrink-0 transition-[width] duration-300 ease-in-out"
        style={{ width: isPinned ? "16rem" : "3rem" }}
      />

      {/* Fixed sidebar — overlays content when hovering in collapsed state */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 flex flex-col bg-sidebar",
          "overflow-hidden transition-[width] duration-300 ease-in-out",
          showFull ? "w-64" : "w-12",
          isOverlay ? "z-50 shadow-2xl" : "z-20"
        )}
        onMouseEnter={() => !isPinned && setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        role="presentation"
      >
        <div className="flex h-full flex-col">
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem
                className={cn(
                  "flex items-center pt-2",
                  showFull ? "px-1.5" : "justify-center px-0"
                )}
              >
                <Link to="/overview">
                  {showFull ? (
                    <Logo className="h-6 w-auto" />
                  ) : (
                    <IconLogo className="h-6 w-auto" />
                  )}
                </Link>
                {showFull && (
                  <button
                    className="ml-auto rounded-md p-1 text-sidebar-foreground/50 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
                    onClick={handleToggle}
                    type="button"
                  >
                    <PanelLeftIcon className="size-4" />
                  </button>
                )}
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>

          <SidebarContent>
            <NavItems items={nav.navHead} showLabel={showFull} />
            <SidebarSeparator className="mx-0" />
            <NavItems
              itemButtonClassName="h-9.5"
              items={nav.navMain}
              showLabel={showFull}
            />
            <NavItems
              className="mt-auto"
              itemButtonClassName="h-8 text-xs"
              items={nav.navTail}
              showLabel={showFull}
            />
          </SidebarContent>

          <SidebarSeparator className="mx-0 my-2" />
          <SidebarFooter>
            {user ? <NavUser showLabel={showFull} user={user} /> : null}
          </SidebarFooter>
        </div>
      </div>
    </>
  );
}
